"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Button } from "../ui/button";
import Link from "next/link";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

const heroContent = [
  {
    src: "/images/hero01.webp",
    alt: "Hero image 1",
    logo: "/Advanced_Fleet_Signs_Logo_White.png",
    logoAlt: "Advanced Fleet Signs Logo",
    logoClass: "object-contain p-4",
    heading: (
      <>
        World Leaders in Fleet Vehicle
        <br className='hidden sm:block' />
        Identification Solutions
      </>
    ),
    description:
      "Custom-made, high-quality identification products for your entire fleet. Ready-to-buy or fully customizable products.",
    buttons: [
      { text: "Shop Now", href: "/rock-board/products", variant: "default" },
      {
        text: "Find a Distributor",
        href: "/distributors",
        variant: "outline",
      },
    ],
  },
  {
    src: "https://res.cloudinary.com/djowccubk/image/upload/v1736320668/images/wcqe9anqyidmbpnwyexd.webp",
    alt: "Rock Board LED Vehicle Identification",
    logo: "/Rock_Board_Gorilla.png",
    logoAlt: "Rock Board Gorilla",
    logoClass: "object-contain p-4",
    heading: (
      <>
        Rock Board
        <sup className='text-sm relative -top-4 md:-top-10 ml-1'>®</sup> LED
        Solutions
      </>
    ),
    description:
      "Revolutionary LED identification systems designed for harsh mining environments. Fully customizable to your fleet's needs.",
    buttons: [
      {
        text: "Custom Products",
        href: "/rock-board/products?type=custom",
        variant: "default",
      },
      {
        text: "Modular Products",
        href: "/rock-board/products?type=modular",
        variant: "outline",
      },
    ],
  },
  {
    src: "https://res.cloudinary.com/djowccubk/image/upload/v1736320844/images/kqkcluffmwhgx5thmarx.webp",
    alt: "XCELR8 Safety Solutions",
    logo: "/XCELR8_X_Logo_Black.png",
    logoAlt: "XCELR8 Logo",
    logoClass: "object-contain p-1",
    heading: (
      <>
        XCELR8<sup className='text-sm relative -top-4 md:-top-10 ml-1'>®</sup>{" "}
        Hi-Vis Solutions
      </>
    ),
    description:
      "Premium reflective signage and safety solutions for maximum visibility in any conditions.",
    buttons: [
      {
        text: "Hi-Vis Products",
        href: "/xcelr8/products?group=hi-vis",
        variant: "default",
      },
      {
        text: "Reflective Tape",
        href: "/xcelr8/products?group=tape",
        variant: "outline",
      },
    ],
  },
];

function HeroSection() {
  const [isClient, setIsClient] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsClient(true);

    // Enhanced scroll container detection for ScrollArea and other containers
    const findScrollContainer = () => {
      let element = containerRef.current?.parentElement;
      while (element) {
        const styles = window.getComputedStyle(element);

        // Check for ScrollArea's scroll container (Radix UI)
        if (element.getAttribute("data-radix-scroll-area-viewport")) {
          return element;
        }

        // Check for any element with scroll capabilities
        if (
          styles.overflowY === "auto" ||
          styles.overflowY === "scroll" ||
          styles.overflow === "auto" ||
          styles.overflow === "scroll"
        ) {
          return element;
        }

        // Check for common scroll container classes
        if (
          element.classList.contains("scroll-area") ||
          element.classList.contains("overflow-y-auto") ||
          element.classList.contains("overflow-auto")
        ) {
          return element;
        }

        element = element.parentElement;
      }
      return window;
    };

    const setupScrollListener = () => {
      const scrollContainer = findScrollContainer();

      const handleScroll = () => {
        if (scrollContainer === window) {
          setScrollY(window.scrollY);
        } else if (scrollContainer instanceof Element) {
          setScrollY(scrollContainer.scrollTop);
        }
      };

      // Add scroll listener
      if (scrollContainer === window) {
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
      } else if (scrollContainer instanceof Element) {
        scrollContainer.addEventListener("scroll", handleScroll, {
          passive: true,
        });
        return () =>
          scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };

    // Try immediately
    let cleanup = setupScrollListener();

    // If no proper container found initially, retry after a short delay
    // This handles cases where the ScrollArea isn't fully mounted yet
    const retryTimeout = setTimeout(() => {
      if (cleanup) cleanup();
      cleanup = setupScrollListener();
    }, 100);

    return () => {
      clearTimeout(retryTimeout);
      if (cleanup) cleanup();
    };
  }, []);

  // Calculate parallax values
  const parallaxY = scrollY * 0.5; // Move at 50% of scroll speed
  const opacity = Math.max(0.3, 1 - scrollY / 800); // Fade out as we scroll

  // Initialize autoplay plugin with proper client-side handling
  const plugin = React.useRef(
    Autoplay({
      delay: 12000,
      stopOnInteraction: true,
    })
  );

  return (
    <section
      ref={containerRef}
      className='w-full h-[69vh] md:h-[80vh] relative overflow-hidden'
    >
      <Carousel
        className='w-full h-full select-none'
        opts={{
          loop: true,
          startIndex: 0,
          skipSnaps: false,
          duration: 20,
        }}
        plugins={isClient ? [plugin.current] : []} // Only add autoplay after hydration
        suppressHydrationWarning
      >
        <CarouselContent className='-ml-0'>
          {heroContent.map((content, index) => (
            <CarouselItem
              key={index}
              className='pl-0 cursor-grab active:cursor-grabbing'
            >
              <div className='relative w-full h-[68vh] md:h-[80vh] select-none overflow-hidden'>
                {/* Parallax Background Image */}
                <div
                  className='absolute inset-0 scale-110' // Scale up to prevent gaps during parallax
                  style={{
                    transform: `translateY(${parallaxY}px)`,
                    opacity: opacity,
                    transition: "none", // Disable transitions for smooth parallax
                  }}
                >
                  <Image
                    src={content.src}
                    alt={content.alt}
                    priority={index === 0}
                    fill
                    quality={index === 0 ? 90 : 60}
                    className='object-cover pointer-events-none select-none'
                    draggable='false'
                    style={{ userSelect: "none", WebkitUserSelect: "none" }}
                    sizes='100vw'
                    loading={index === 0 ? "eager" : "lazy"}
                    {...(index !== 0 && {
                      placeholder: "blur",
                      blurDataURL:
                        "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyb5bt/KqklfidRJqr4jt7VeBw5qWvXKHw/b6PZW8tlpuq2xz1R0/j/2Q==",
                    })}
                  />
                </div>

                {/* Static overlay and content */}
                <div className='absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center'>
                  <div
                    className='flex flex-col items-center justify-center space-y-4 md:space-y-8 text-center max-w-[1200px] px-8 md:px-20 lg:px-24 xl:px-32 py-6 md:py-0 -mt-16 md:-mt-10 select-none'
                    style={{ userSelect: "none", WebkitUserSelect: "none" }}
                  >
                    {content.logo && (
                      <div className='h-32 w-64 md:h-48 md:w-96'>
                        <div className='relative w-full h-full'>
                          <Image
                            src={content.logo}
                            alt={content.logoAlt}
                            fill
                            quality={85}
                            priority={index === 0}
                            loading={index === 0 ? "eager" : "lazy"}
                            className={cn(
                              content.logoClass,
                              "pointer-events-none select-none"
                            )}
                            draggable='false'
                            style={{
                              userSelect: "none",
                              WebkitUserSelect: "none",
                            }}
                            sizes='(max-width: 768px) 256px, 384px'
                          />
                        </div>
                      </div>
                    )}
                    <div
                      className='space-y-2 select-none'
                      style={{ userSelect: "none", WebkitUserSelect: "none" }}
                    >
                      <h1 className='text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none text-white px-4'>
                        {content.heading}
                      </h1>
                      <p className='mx-auto max-w-[700px] text-gray-200 text-sm md:text-xl px-4'>
                        {content.description}
                      </p>
                    </div>
                    <div className='space-x-2 md:space-x-4 [&_*]:cursor-pointer'>
                      {content.buttons.map((button, buttonIndex) => (
                        <Link key={buttonIndex} href={button.href} passHref>
                          <Button
                            size='default'
                            className='text-sm md:text-base'
                            variant={button.variant as "default" | "outline"}
                          >
                            {button.text}
                          </Button>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className='hidden md:flex left-4 z-20 cursor-pointer' />
        <CarouselNext className='hidden md:flex right-4 z-20 cursor-pointer' />
      </Carousel>
    </section>
  );
}

export default HeroSection;
